import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Select,
  Typography,
  Modal,
  Form,
  InputNumber,
  Input,
  Row,
  Col,
  Statistic,
  Empty,
} from "antd";
import {
  InboxOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getExcessStock,
  createExcessStock,
  findMatchingExcess,
  applyExcessStock,
} from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text } = Typography;

const ExcessStock = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const navigate = useNavigate();
  const [data, setData] = useState({ excess: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [filterStatus, setFilterStatus] = useState(undefined);
  const [filterPly, setFilterPly] = useState(undefined);

  // Create modal
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);

  // Use-stock modal
  const [useModalVisible, setUseModalVisible] = useState(false);
  const [selectedExcess, setSelectedExcess] = useState(null);
  const [useForm] = Form.useForm();
  const [useLoading, setUseLoading] = useState(false);

  // Find-match modal
  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [matchForm] = Form.useForm();
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchResults, setMatchResults] = useState(null);

  // ── Fetch ──────────────────────────────────────────────────
  const fetchExcess = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        count: pagination.pageSize,
      };
      if (filterStatus) params.status = filterStatus;
      if (filterPly) params.ply = filterPly;

      const result = await getExcessStock(params);
      setData(result);
    } catch (error) {
      console.log(error.message);
      toast.error("Failed to load excess stock");
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filterStatus, filterPly]);

  useEffect(() => {
    fetchExcess();
  }, [fetchExcess]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Create excess ──────────────────────────────────────────
  const handleCreate = async (values) => {
    try {
      setCreateLoading(true);
      await createExcessStock(values);
      toast.success("Excess stock entry created");
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchExcess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create excess stock");
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Use excess ─────────────────────────────────────────────
  const openUseModal = (record) => {
    setSelectedExcess(record);
    useForm.setFieldsValue({ quantity: record.available_qty });
    setUseModalVisible(true);
  };

  const handleUse = async (values) => {
    if (!selectedExcess) return;
    try {
      setUseLoading(true);
      await applyExcessStock(selectedExcess.id, {
        job_id: values.job_id,
        quantity: values.quantity,
      });
      toast.success("Excess stock assigned to job");
      setUseModalVisible(false);
      useForm.resetFields();
      setSelectedExcess(null);
      fetchExcess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to use excess stock");
    } finally {
      setUseLoading(false);
    }
  };

  // ── Find match ─────────────────────────────────────────────
  const handleFindMatch = async (values) => {
    try {
      setMatchLoading(true);
      const result = await findMatchingExcess(values);
      setMatchResults(result);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to find matches");
    } finally {
      setMatchLoading(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────
  const columns = [
    {
      title: "Source Job",
      key: "source",
      width: 150,
      render: (_, r) =>
        r.source_job?.job_number ? (
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/job/${r.source_job_id || ""}`)}
            style={{ padding: 0 }}
          >
            {r.source_job.job_number}
          </Button>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Stage",
      key: "stage",
      width: 130,
      render: (_, r) =>
        r.stage?.name ? <Tag>{r.stage.name}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: "Specs",
      key: "specs",
      width: 200,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.ply && <Tag>{r.ply} Ply</Tag>}
          {r.roll_size && <Tag>RS {r.roll_size}&quot;</Tag>}
          {r.cutting_size && <Tag>CS {r.cutting_size}&quot;</Tag>}
          {r.flute_type && <Tag>Flute {r.flute_type}</Tag>}
        </Space>
      ),
    },
    {
      title: "Original Qty",
      dataIndex: "original_qty",
      key: "original_qty",
      width: 110,
      render: (v) => v?.toLocaleString() || 0,
    },
    {
      title: "Available",
      dataIndex: "available_qty",
      key: "available_qty",
      width: 110,
      render: (v) => (
        <Text strong style={{ color: v > 0 ? "#52c41a" : "#999" }}>
          {v?.toLocaleString() || 0}
        </Text>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, r) => {
        // Derive status from available_qty if not explicitly present
        const status =
          r.status || (r.available_qty > 0 ? "available" : "used");
        const color = {
          available: "success",
          used: "default",
          expired: "error",
        }[status] || "default";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, r) =>
        (r.available_qty || 0) > 0 ? (
          <Button
            size="small"
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => openUseModal(r)}
          >
            Assign
          </Button>
        ) : (
          <Text type="secondary">Used</Text>
        ),
    },
  ];

  return (
    <>
      <PageTitle title="Back" subtitle="Excess / WIP Stock" />

      <Card
        title={
          <Space>
            <InboxOutlined style={{ color: "#1890ff" }} />
            <span>Semi-Processed &amp; Excess Stock</span>
            <Tag>{data.total} items</Tag>
          </Space>
        }
        extra={
          <Space>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 130 }}
              value={filterStatus}
              onChange={(v) => {
                setFilterStatus(v);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            >
              <Select.Option value="available">Available</Select.Option>
              <Select.Option value="used">Used</Select.Option>
              <Select.Option value="expired">Expired</Select.Option>
            </Select>
            <Select
              placeholder="Ply"
              allowClear
              style={{ width: 90 }}
              value={filterPly}
              onChange={(v) => {
                setFilterPly(v);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            >
              <Select.Option value={3}>3 Ply</Select.Option>
              <Select.Option value={5}>5 Ply</Select.Option>
              <Select.Option value={7}>7 Ply</Select.Option>
            </Select>
            <Button
              icon={<SearchOutlined />}
              onClick={() => {
                setMatchResults(null);
                setMatchModalVisible(true);
              }}
            >
              Find Match
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Add Excess
            </Button>
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchExcess}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data.excess || []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) =>
              setPagination({ current: page, pageSize }),
          }}
          size="middle"
        />
      </Card>

      {/* ── Create Excess Modal ───────────────────────────────── */}
      <Modal
        title="Add Excess Stock Entry"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="source_job_id"
                label="Source Job ID"
                rules={[{ required: true, message: "Enter source job ID" }]}
              >
                <Input placeholder="e.g. job_1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="stage_id"
                label="Stage ID"
                rules={[{ required: true, message: "Enter stage ID" }]}
              >
                <Input placeholder="e.g. stage_casting" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true }, { type: "number", min: 1 }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="roll_size" label="Roll Size (inches)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cutting_size" label="Cutting Size (inches)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="ply"
                label="Ply"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select ply">
                  <Select.Option value={3}>3 Ply</Select.Option>
                  <Select.Option value={5}>5 Ply</Select.Option>
                  <Select.Option value={7}>7 Ply</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="flute_type" label="Flute Type">
                <Select placeholder="Select" allowClear>
                  <Select.Option value="B">B Flute</Select.Option>
                  <Select.Option value="C">C Flute</Select.Option>
                  <Select.Option value="E">E Flute</Select.Option>
                  <Select.Option value="BC">BC Flute</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cost_per_unit" label="Cost / Unit (₹)">
                <InputNumber min={0} step={0.5} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="is_customer_specific" label="Customer-Specific?" initialValue={false}>
            <Select>
              <Select.Option value={false}>No (general)</Select.Option>
              <Select.Option value={true}>Yes</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                Create Entry
              </Button>
              <Button onClick={() => setCreateModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Use Excess Modal ──────────────────────────────────── */}
      <Modal
        title={`Assign Excess Stock${selectedExcess?.source_job?.job_number ? ` from ${selectedExcess.source_job.job_number}` : ""}`}
        open={useModalVisible}
        onCancel={() => {
          setUseModalVisible(false);
          useForm.resetFields();
          setSelectedExcess(null);
        }}
        footer={null}
        destroyOnClose
      >
        {selectedExcess && (
          <div style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Available"
                  value={selectedExcess.available_qty}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic title="Ply" value={selectedExcess.ply || "-"} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Roll Size"
                  value={selectedExcess.roll_size ? `${selectedExcess.roll_size}"` : "-"}
                />
              </Col>
            </Row>
          </div>
        )}
        <Form form={useForm} layout="vertical" onFinish={handleUse}>
          <Form.Item
            name="job_id"
            label="Assign to Job ID"
            rules={[{ required: true, message: "Enter the target job ID" }]}
          >
            <Input placeholder="e.g. job_2" />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[
              { required: true },
              { type: "number", min: 1 },
              {
                type: "number",
                max: selectedExcess?.available_qty || 0,
                message: `Max ${selectedExcess?.available_qty || 0}`,
              },
            ]}
          >
            <InputNumber
              min={1}
              max={selectedExcess?.available_qty || 999999}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={useLoading}>
                Assign to Job
              </Button>
              <Button onClick={() => setUseModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Find Matching Excess Modal ────────────────────────── */}
      <Modal
        title="Find Matching Excess Stock"
        open={matchModalVisible}
        onCancel={() => {
          setMatchModalVisible(false);
          matchForm.resetFields();
          setMatchResults(null);
        }}
        footer={null}
        destroyOnClose
        width={650}
      >
        <Form form={matchForm} layout="vertical" onFinish={handleFindMatch}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="roll_size"
                label="Roll Size"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} placeholder='inches' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="cutting_size"
                label="Cutting Size"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} placeholder='inches' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ply"
                label="Ply"
                rules={[{ required: true }]}
              >
                <Select placeholder="Ply">
                  <Select.Option value={3}>3</Select.Option>
                  <Select.Option value={5}>5</Select.Option>
                  <Select.Option value={7}>7</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="flute_type" label="Flute Type">
                <Select allowClear placeholder="Any">
                  <Select.Option value="B">B</Select.Option>
                  <Select.Option value="C">C</Select.Option>
                  <Select.Option value="E">E</Select.Option>
                  <Select.Option value="BC">BC</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="quantity_needed"
                label="Qty Needed"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="customer_id" label="Customer ID">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={matchLoading}>
              Search
            </Button>
          </Form.Item>
        </Form>

        {/* Match results */}
        {matchResults && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <Statistic
                  title="Available"
                  value={matchResults.total_available || 0}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Can Fulfill"
                  value={matchResults.can_fulfill ? "Yes" : "No"}
                  valueStyle={{
                    color: matchResults.can_fulfill ? "#52c41a" : "#ff4d4f",
                  }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Fulfillment"
                  value={matchResults.fulfillment_pct || 0}
                  suffix="%"
                />
              </Col>
            </Row>
            {matchResults.candidates?.length > 0 ? (
              <Table
                dataSource={matchResults.candidates}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Source",
                    render: (_, r) => r.source_job?.job_number || "-",
                  },
                  { title: "Available", dataIndex: "available_qty" },
                  { title: "Ply", dataIndex: "ply" },
                  {
                    title: "Specs",
                    render: (_, r) =>
                      `RS ${r.roll_size || "-"}" × CS ${r.cutting_size || "-"}"`,
                  },
                  {
                    title: "",
                    render: (_, r) =>
                      (r.available_qty || 0) > 0 && (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            setMatchModalVisible(false);
                            openUseModal(r);
                          }}
                        >
                          Use
                        </Button>
                      ),
                  },
                ]}
              />
            ) : (
              <Empty description="No matching excess stock found" />
            )}
          </Card>
        )}
      </Modal>
    </>
  );
};

export default ExcessStock;
